/* NFPA 1010 (Jones & Bartlett) skill-drill catalogue — Firefighter I and Firefighter II.
 *
 * The third recruit program beside VIERA (./skillDrills.js) and the OFC Driver/Operator
 * sheet (./skillDrillsDriverOperator.js). Departments that train from Jones & Bartlett's
 * *Fundamentals of Firefighter Skills and Hazardous Materials Response, Canadian Fifth
 * Edition* pick the `nfpa1010` training provider, and these two sections become their
 * Requirements ladder. 150 Mile House FD is the first.
 *
 * Generated once from the J&B skill sheets (drill numbers, titles and the JPR printed on
 * each sheet) and maintained here by hand from then on — unlike ./skillDrills.js there is
 * no regeneration step to undo an edit. Not tenant-editable.
 *
 * Structure, all of it J&B's own:
 *
 *  - CHAPTERS are the book's chapters and back one matrix cell each
 *    (`skills.ff1.ch3Ppe`, …). The matrix columns are built from these same objects (see
 *    nfpa1010LevelSkills in ./index.js), so a cell and its drills cannot drift apart.
 *    Chapters with no skill drills (1, 5, 6, 20, 24, 28) are theory only.
 *  - MODULES group chapters for teaching: each one is a block of practice nights and a
 *    single written exam, so `exam` is the module number and one exam covers every
 *    chapter in its module. The split follows the department's own program (Module 1 is
 *    chapters 1-4, Module 2 chapters 5-6, … Module 8 chapters 25-28).
 *  - SECTIONS are the qualification levels. Every sheet in chapters 1-21 cites a
 *    Firefighter I JPR and every sheet in 22-28 a Firefighter II one, so Modules 1-6 are
 *    Firefighter I and Modules 7-8 Firefighter II, with no module straddling the line.
 *
 * Module 9 (Hazmat Awareness & Operations) is a stand-alone course and isn't a section
 * here — Hazmat Operations is a certificate on Firefighter II (see ./index.js).
 *
 * Drill codes are `jb-<chapter>-<n>`, J&B's own Skill Drill number with a prefix. The
 * prefix is load-bearing: VIERA's checklist steps and skill sheets are looked up by bare
 * drill code (`4-1`, …), so an unprefixed J&B code would silently pick up a VIERA sheet
 * for an unrelated drill. Codes must stay stable — drills are stored by code at
 * `skills.<level>.<skillKey>.practical.drills[<code>]`.
 *
 * `documentSet: 'jb-1010'` tells the sign-off UI and the api that VIERA's documents don't
 * apply. Each drill is signed off against its J&B sheet's numbered task steps
 * (./nfpa1010SkillSheetSteps.js), and `sheetEdition` names the edition of those sheets —
 * it's stamped on every sign-off, so a later edition can't change the steps a member
 * was assessed on. The sheets themselves (blank and filled with a member's marks) come
 * from nfpa1010-skill-sheets/ — see ./nfpa1010SkillSheets.js. VIERA's evaluation forms
 * don't apply.
 */

// Which ladder these sections are rungs of. Sections without a `ladder` are VIERA's.
const LADDER = 'nfpa1010';

const FF1_CHAPTERS = [
    {
        chapter: 1,
        label: 'The Fire Service',
        group: 'ff1',
        skillKey: 'ch1FireService',
        module: 1,
        exam: 1,
        theoryOnly: true,
        drills: []
    },
    {
        chapter: 2,
        label: 'Firefighter Health and Safety',
        matrixLabel: 'FF Health & Safety',
        group: 'ff1',
        skillKey: 'ch2Safety',
        module: 1,
        exam: 1,
        drills: [
            {code: 'jb-2-1', label: '2-1', name: 'Mounting Apparatus', standard: '6.3.2'},
            {code: 'jb-2-2', label: '2-2', name: 'Dismounting a Stopped Apparatus', standard: '6.3.2'}
        ]
    },
    {
        chapter: 3,
        label: 'Personal Protective Equipment',
        matrixLabel: 'PPE & SCBA',
        group: 'ff1',
        skillKey: 'ch3Ppe',
        module: 1,
        exam: 1,
        drills: [
            {code: 'jb-3-1', label: '3-1', name: 'Donning Personal Protective Clothing', standard: '6.1.2'},
            {code: 'jb-3-2', label: '3-2', name: 'Doffing Personal Protective Clothing', standard: '6.1.2'},
            {code: 'jb-3-3', label: '3-3', name: 'Donning an SCBA from an Apparatus Seat Mount', standard: '6.3.1'},
            {code: 'jb-3-4', label: '3-4', name: 'Donning an SCBA from an Apparatus Compartment Mount', standard: '6.3.1'},
            {code: 'jb-3-5', label: '3-5', name: 'Donning an SCBA Using the Over-the-Head Method', standard: '6.3.1'},
            {code: 'jb-3-6', label: '3-6', name: 'Donning an SCBA Using the Coat Method', standard: '6.3.1'},
            {code: 'jb-3-7', label: '3-7', name: 'Donning a Face Piece', standard: '6.3.1'},
            {code: 'jb-3-8', label: '3-8', name: 'Doffing an SCBA', standard: '6.3.1'},
            {code: 'jb-3-9', label: '3-9', name: 'Visual SCBA Inspection', standard: '6.5.1'},
            {code: 'jb-3-10', label: '3-10', name: 'SCBA Operational Inspection', standard: '6.5.1'},
            {code: 'jb-3-11', label: '3-11', name: 'Replacing an SCBA Cylinder', standard: '6.3.1'},
            {code: 'jb-3-12', label: '3-12', name: 'Replacing an SCBA Cylinder on Another Firefighter', standard: '6.3.1'},
            {code: 'jb-3-13', label: '3-13', name: 'Refilling SCBA Cylinders', standard: '6.5.1'},
            {code: 'jb-3-14', label: '3-14', name: 'Cleaning an SCBA', standard: '6.5.1'}
        ]
    },
    {
        chapter: 4,
        label: 'Fire Service Communications',
        matrixLabel: 'Fire Service Comms',
        width: 120,
        group: 'ff1',
        skillKey: 'ch4Comms',
        module: 1,
        exam: 1,
        drills: [
            {code: 'jb-4-1', label: '4-1', name: 'Receiving a Call at a Fire Station and Initiating a Response to an Emergency', standard: '6.2.1, 6.2.2'},
            {code: 'jb-4-2', label: '4-2', name: 'Using a Radio', standard: '6.2.2'}
        ]
    },
    {
        chapter: 5,
        label: 'Fire Behaviour',
        group: 'ff1',
        skillKey: 'ch5FireBehaviour',
        module: 2,
        exam: 2,
        theoryOnly: true,
        drills: []
    },
    {
        chapter: 6,
        label: 'Building Construction',
        width: 130,
        group: 'ff1',
        skillKey: 'ch6Building',
        module: 2,
        exam: 2,
        theoryOnly: true,
        drills: []
    },
    {
        chapter: 7,
        label: 'Firefighter Tools and Equipment',
        matrixLabel: 'FF Tools & Equipment',
        group: 'ff1',
        skillKey: 'ch7Tools',
        module: 3,
        exam: 3,
        drills: [
            {code: 'jb-7-1', label: '7-1', name: 'Cleaning and Inspecting Hand Tools', standard: '6.5.1'}
        ]
    },
    {
        chapter: 8,
        label: 'Ropes and Knots',
        matrixLabel: 'Ropes & Knots',
        group: 'ff1',
        skillKey: 'ch8Ropes',
        module: 3,
        exam: 3,
        drills: [
            {code: 'jb-8-1', label: '8-1', name: 'Caring for Life Safety Ropes', standard: '6.5.1'},
            {code: 'jb-8-2', label: '8-2', name: 'Cleaning Fire Department Ropes', standard: '6.5.1'},
            {code: 'jb-8-3', label: '8-3', name: 'Inspecting Fire Department Ropes', standard: '6.5.1'},
            {code: 'jb-8-4', label: '8-4', name: 'Placing a Life Safety Rope in a Rope Bag', standard: '6.5.1'},
            {code: 'jb-8-5', label: '8-5', name: 'Tying an Overhand Knot as a Safety Knot', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-6', label: '8-6', name: 'Tying a Figure Eight Knot', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-7', label: '8-7', name: 'Tying a Half Hitch Around an Object', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-8', label: '8-8', name: 'Tying a Clove Hitch in the Open', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-9', label: '8-9', name: 'Tying a Clove Hitch Around an Object', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-10', label: '8-10', name: 'Tying a Figure Eight on a Bight', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-11', label: '8-11', name: 'Tying a Figure Eight Follow-Through', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-12', label: '8-12', name: 'Tying a Bowline', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-13', label: '8-13', name: 'Tying a Sheet Bend', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-14', label: '8-14', name: 'Tying a Figure Eight Bend', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-15', label: '8-15', name: 'Tying a Water Knot', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-16', label: '8-16', name: 'Hoisting an Axe', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-17', label: '8-17', name: 'Hoisting a Pike Pole', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-18', label: '8-18', name: 'Hoisting a Ladder', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-19', label: '8-19', name: 'Hoisting a Charged Hose Line', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-20', label: '8-20', name: 'Hoisting an Uncharged Hose Line', standard: '6.1.2, 6.3.20'},
            {code: 'jb-8-21', label: '8-21', name: 'Hoisting an Exhaust Fan or Power Tool', standard: '6.1.2, 6.3.20'}
        ]
    },
    {
        chapter: 9,
        label: 'Portable Fire Extinguishers',
        matrixLabel: 'Portable Extinguishers',
        width: 120,
        group: 'ff1',
        skillKey: 'ch9Extinguishers',
        module: 3,
        exam: 3,
        drills: [
            {code: 'jb-9-1', label: '9-1', name: 'Transporting a Fire Extinguisher', standard: '6.3.16'},
            {code: 'jb-9-2', label: '9-2', name: 'Extinguishing a Class A Fire with a Stored-Pressure Water-Type Fire Extinguisher', standard: '6.3.16'},
            {code: 'jb-9-3', label: '9-3', name: 'Extinguishing a Class A Fire with a Multipurpose Dry-Chemical Fire Extinguisher', standard: '6.3.16'},
            {code: 'jb-9-4', label: '9-4', name: 'Extinguishing a Class B Flammable Liquid Fire with a Dry-Chemical Fire Extinguisher', standard: '6.3.16'},
            {code: 'jb-9-5', label: '9-5', name: 'Extinguishing a Class B Flammable Liquid Fire with a Stored-Pressure Foam Fire Extinguisher (AFFF or FFFP)', standard: '6.3.16'},
            {code: 'jb-9-6', label: '9-6', name: 'Operating a Carbon Dioxide Fire Extinguisher', standard: '6.3.16'},
            {code: 'jb-9-7', label: '9-7', name: 'Operating a Halogenated Stored-Pressure Fire Extinguisher', standard: '6.3.16'},
            {code: 'jb-9-8', label: '9-8', name: 'Extinguishing an Incipient Stage Fire Involving Combustible Metal Filings or Shavings with a Dry-Powder Fire Extinguisher', standard: '6.3.16'},
            {code: 'jb-9-9', label: '9-9', name: 'Extinguishing a Fire in a Deep-Fat Fryer with a Wet-Chemical Fire Extinguisher', standard: '6.3.16'}
        ]
    },
    {
        chapter: 10,
        label: 'Water Supply Systems',
        matrixLabel: 'Water Supply',
        group: 'ff1',
        skillKey: 'ch10WaterSupply',
        module: 3,
        exam: 3,
        drills: [
            {code: 'jb-10-1', label: '10-1', name: 'Opening a Dry Barrel Fire Hydrant', standard: '6.3.15'},
            {code: 'jb-10-2', label: '10-2', name: 'Shutting Down a Dry Barrel Fire Hydrant', standard: '6.3.15'},
            {code: 'jb-10-3', label: '10-3', name: 'Operating a Wet Barrel Fire Hydrant', standard: '6.3.15'},
            {code: 'jb-10-4', label: '10-4', name: 'Shutting Down a Wet Barrel Fire Hydrant', standard: '6.3.15'},
            {code: 'jb-10-5', label: '10-5', name: 'Conducting a Fire Hydrant Flow Test Using a Pitot Gauge', standard: ''},
            {code: 'jb-10-6', label: '10-6', name: 'Assisting the Pump Operator with Drafting', standard: '6.3.15'},
            {code: 'jb-10-7', label: '10-7', name: 'Setting Up a Portable Tank', standard: '6.3.15'}
        ]
    },
    {
        chapter: 11,
        label: 'Fire Hose, Appliances, and Nozzles',
        matrixLabel: 'Hose & Nozzles',
        group: 'ff1',
        skillKey: 'ch11Hose',
        module: 4,
        exam: 4,
        drills: [
            {code: 'jb-11-1', label: '11-1', name: 'Replacing the Swivel Gasket', standard: '6.5.2'},
            {code: 'jb-11-2', label: '11-2', name: 'Performing the One-Firefighter Foot-Tilt Method of Coupling a Fire Hose with Threaded Couplings', standard: '6.3.10'},
            {code: 'jb-11-3', label: '11-3', name: 'Performing the Two-Firefighter Method of Coupling a Fire Hose with Threaded Couplings', standard: '6.3.10'},
            {code: 'jb-11-4', label: '11-4', name: 'Performing the One-Firefighter Knee-Press Method of Uncoupling a Fire Hose with Threaded Couplings', standard: '6.3.10'},
            {code: 'jb-11-5', label: '11-5', name: 'Performing the Two-Firefighter Stiff-Arm Method of Uncoupling a Fire Hose with Threaded Couplings', standard: '6.3.10'},
            {code: 'jb-11-6', label: '11-6', name: 'Uncoupling a Hose with Threaded Couplings Using a Spanner Wrench (Hose Key)', standard: '6.3.10'},
            {code: 'jb-11-7', label: '11-7', name: 'Performing a Straight or Storage Hose Roll', standard: '6.5.2'},
            {code: 'jb-11-8', label: '11-8', name: 'Performing a Single-Doughnut Hose Roll', standard: '6.5.2'},
            {code: 'jb-11-9', label: '11-9', name: 'Performing a Twin-Doughnut Hose Roll', standard: '6.5.2'},
            {code: 'jb-11-10', label: '11-10', name: 'Performing a Self-Locking Twin-Doughnut Hose Roll', standard: '6.5.2'},
            {code: 'jb-11-11', label: '11-11', name: 'Operating a Smooth-Bore Nozzle', standard: '6.3.10'},
            {code: 'jb-11-12', label: '11-12', name: 'Operating a Fog-Stream Nozzle', standard: '6.3.10'},
            {code: 'jb-11-13', label: '11-13', name: 'Cleaning and Drying Hose', standard: '6.5.2'},
            {code: 'jb-11-14', label: '11-14', name: 'Inspecting a Hose for Defects and Marking a Defective Hose', standard: '6.5.2'}
        ]
    },
    {
        chapter: 12,
        label: 'Ladders',
        group: 'ff1',
        skillKey: 'ch12Ladders',
        module: 4,
        exam: 4,
        drills: [
            {code: 'jb-12-1', label: '12-1', name: 'Clean, Inspect, Maintain, and Store a Ladder', standard: '6.5.1'},
            {code: 'jb-12-2', label: '12-2', name: 'One-Firefighter Shoulder Carry', standard: '6.3.6'},
            {code: 'jb-12-3', label: '12-3', name: 'Two- and Three-Firefighter Shoulder Carry', standard: '6.3.6'},
            {code: 'jb-12-4', label: '12-4', name: 'Two- and Three-Firefighter Suitcase Carry', standard: '6.3.6'},
            {code: 'jb-12-5', label: '12-5', name: 'Three- and Four-Firefighter Flat Carry', standard: '6.3.6'},
            {code: 'jb-12-6', label: '12-6', name: 'Three- and Four-Firefighter Flat Shoulder Carry', standard: '6.3.6'},
            {code: 'jb-12-7', label: '12-7', name: 'Tying the Halyard', standard: '6.3.6'},
            {code: 'jb-12-8', label: '12-8', name: 'One-Firefighter Flat Raise for Ladders Less Than 4 m (14 Feet) Long', standard: '6.3.6'},
            {code: 'jb-12-9', label: '12-9', name: 'Two-Firefighter Flat Raise', standard: '6.3.6'},
            {code: 'jb-12-10', label: '12-10', name: 'Two-Firefighter Beam Raise', standard: '6.3.6'},
            {code: 'jb-12-11', label: '12-11', name: 'Three-Firefighter Flat Raise', standard: '6.3.6'},
            {code: 'jb-12-12', label: '12-12', name: 'Four-Firefighter Flat Raise', standard: '6.3.6'},
            {code: 'jb-12-13', label: '12-13', name: 'Climbing a Ladder While Carrying a Tool', standard: '6.3.12'},
            {code: 'jb-12-14', label: '12-14', name: 'Using a Leg Lock to Work from a Ladder', standard: '6.3.6'},
            {code: 'jb-12-15', label: '12-15', name: 'Deploying and Removing a Roof Ladder', standard: '6.3.12'}
        ]
    },
    {
        chapter: 13,
        label: 'Supply Line and Attack Line Evolutions',
        matrixLabel: 'Supply Line & Attack',
        width: 120,
        group: 'ff1',
        skillKey: 'ch13Evolutions',
        module: 4,
        exam: 4,
        drills: [
            {code: 'jb-13-1', label: '13-1', name: 'Attaching a Soft Sleeve Hose to a Fire Hydrant', standard: '6.3.15'},
            {code: 'jb-13-2', label: '13-2', name: 'Performing a Forward Hose Lay from a Hydrant', standard: '6.3.15'},
            {code: 'jb-13-3', label: '13-3', name: 'Performing a Reverse Hose Lay with Two Pumpers', standard: '6.3.15'},
            {code: 'jb-13-4', label: '13-4', name: 'Performing a Split Hose Lay', standard: '6.3.15'},
            {code: 'jb-13-5', label: '13-5', name: 'Using a Four-Way Hydrant Valve', standard: '6.3.15'},
            {code: 'jb-13-6', label: '13-6', name: 'Performing a Flat Hose Load', standard: '6.5.2'},
            {code: 'jb-13-7', label: '13-7', name: 'Performing a Horseshoe Hose Load', standard: '6.5.2'},
            {code: 'jb-13-8', label: '13-8', name: 'Performing an Accordion Hose Load', standard: '6.5.2'},
            {code: 'jb-13-9', label: '13-9', name: 'Performing a Working Hose Drag', standard: '6.3.10'},
            {code: 'jb-13-10', label: '13-10', name: 'Performing a Shoulder Carry from a Flat or Horseshoe Load', standard: '6.3.10'},
            {code: 'jb-13-11', label: '13-11', name: 'Advancing an Accordion Load', standard: '6.3.10'},
            {code: 'jb-13-12', label: '13-12', name: 'Performing a Preconnected Flat Hose Load', standard: '6.5.2'},
            {code: 'jb-13-13', label: '13-13', name: 'Performing a Minuteman Hose Load', standard: '6.5.2'},
            {code: 'jb-13-14', label: '13-14', name: 'Performing a Triple-Layer Hose Load', standard: '6.5.2'},
            {code: 'jb-13-15', label: '13-15', name: 'Deploying and Advancing a Minuteman Hose Load', standard: '6.3.10'},
            {code: 'jb-13-16', label: '13-16', name: 'Deploying and Advancing a Preconnected Flat Hose Load', standard: '6.3.10'},
            {code: 'jb-13-17', label: '13-17', name: 'Deploying and Advancing a Triple-Layer Hose Load', standard: '6.3.10'},
            {code: 'jb-13-18', label: '13-18', name: 'Charging the Hose Line and Preparing to Attack the Fire', standard: '6.5.2'},
            {code: 'jb-13-19', label: '13-19', name: 'Deploying and Advancing Wyed Lines', standard: '6.3.10'},
            {code: 'jb-13-20', label: '13-20', name: 'Advancing an Uncharged Attack Line Up a Stairway', standard: '6.3.10'},
            {code: 'jb-13-21', label: '13-21', name: 'Advancing a Charged Attack Line Down a Stairway', standard: '6.3.10'},
            {code: 'jb-13-22', label: '13-22', name: 'Advancing an Uncharged Attack Line up a Ladder', standard: '6.3.10'},
            {code: 'jb-13-23', label: '13-23', name: 'Operating an Attack Line from a Ladder', standard: '6.3.10'},
            {code: 'jb-13-24', label: '13-24', name: 'Connecting a Hose Line to Supply a Fire Department Connection', standard: '6.5.2'},
            {code: 'jb-13-25', label: '13-25', name: 'Connecting and Advancing an Attack Line from a Standpipe Outlet', standard: '6.3.10'},
            {code: 'jb-13-26', label: '13-26', name: 'Replacing a Damaged Section of Hose', standard: '6.3.10, 6.5.2'},
            {code: 'jb-13-27', label: '13-27', name: 'Draining a Hose', standard: '6.5.2'},
            {code: 'jb-13-28', label: '13-28', name: 'Unload and Clean a Hose Bed', standard: '6.5.2'}
        ]
    },
    {
        chapter: 14,
        label: 'Fire Suppression',
        width: 130,
        group: 'ff1',
        skillKey: 'ch14Suppression',
        module: 5,
        exam: 5,
        drills: [
            {code: 'jb-14-1', label: '14-1', name: 'Preparing to Make an Interior, Offensive Fire Attack', standard: '6.3.10'},
            {code: 'jb-14-2', label: '14-2', name: 'Performing a Direct Attack', standard: '6.3.10'},
            {code: 'jb-14-3', label: '14-3', name: 'Performing an Indirect Attack', standard: '6.3.10'},
            {code: 'jb-14-4', label: '14-4', name: 'Performing a Combination Attack', standard: '6.3.10'},
            {code: 'jb-14-5', label: '14-5', name: 'Performing a Transitional Attack', standard: '6.3.10'},
            {code: 'jb-14-6', label: '14-6', name: 'Performing the One-Firefighter Method for Operating a Large Handline', standard: '6.3.8'},
            {code: 'jb-14-7', label: '14-7', name: 'Performing the Two-Firefighter Method for Operating a Large Handline', standard: '6.3.8'},
            {code: 'jb-14-8', label: '14-8', name: 'Operating a Portable Monitor', standard: '6.3.8'},
            {code: 'jb-14-9', label: '14-9', name: 'Operating a Deck Gun', standard: '6.3.8'},
            {code: 'jb-14-10', label: '14-10', name: 'Shutting Off Gas Utilities', standard: '6.3.18'},
            {code: 'jb-14-11', label: '14-11', name: 'Shutting Off Electrical Utilities', standard: '6.3.18'},
            {code: 'jb-14-12', label: '14-12', name: 'Locating and Suppressing Concealed-Space Fires', standard: '6.3.8'},
            {code: 'jb-14-13', label: '14-13', name: 'Extinguishing an Outside Class A Fire', standard: '6.3.8'},
            {code: 'jb-14-14', label: '14-14', name: 'Extinguishing a Vehicle Fire', standard: '6.3.7'},
            {code: 'jb-14-15', label: '14-15', name: 'Immobilize and Disable a Vehicle Following a Vehicle Fire or a Motor Vehicle Accident', standard: '6.3.7'}
        ]
    },
    {
        chapter: 15,
        label: 'Forcible Entry',
        group: 'ff1',
        skillKey: 'ch15ForcibleEntry',
        module: 5,
        exam: 5,
        drills: [
            {code: 'jb-15-1', label: '15-1', name: 'Forcing an Inward-Swinging Door Set in a Wood Frame with a Tubular Deadbolt Using the Sledgehammer Method', standard: '6.3.4'},
            {code: 'jb-15-2', label: '15-2', name: 'Forcing an Inward-Swinging Door Set in a Wood Frame Using the Stutter-Step Method', standard: '6.3.4'},
            {code: 'jb-15-3', label: '15-3', name: 'Using the GSF Technique and a Halligan Fork on an Outward-Swinging Door', standard: '6.3.4'},
            {code: 'jb-15-4', label: '15-4', name: 'Using the GSF Technique and a Halligan Adze on an Outward-Swinging Door', standard: '6.3.4'},
            {code: 'jb-15-5', label: '15-5', name: 'Using the GSF Technique and a Halligan Fork on an Inward-Swinging Door', standard: '6.3.4'},
            {code: 'jb-15-6', label: '15-6', name: 'Using the GSF Technique with a Halligan Adze on an Inward-Swinging Door', standard: '6.3.4'},
            {code: 'jb-15-7', label: '15-7', name: 'Using the GSF Technique on an Inward-Swinging Door with a Hydra-Ram or Hydraulic Spreaders with Door-Opening Tips', standard: '6.3.4'},
            {code: 'jb-15-8', label: '15-8', name: 'Forcing a Bored Lockset with an S&D or Rex Tool', standard: '6.3.4'},
            {code: 'jb-15-9', label: '15-9', name: 'Forcing a Bored Lockset with an A Tool or Halligan Adze', standard: '6.3.4'},
            {code: 'jb-15-10', label: '15-10', name: 'Forcing a Mortise Lock with a K Tool, an S&D Tool, or a Rex Tool', standard: '6.3.4'},
            {code: 'jb-15-11', label: '15-11', name: 'Forcing a Mortise Lock with Locking Pliers or Angled Slip-Joint Pliers', standard: '6.3.4'},
            {code: 'jb-15-12', label: '15-12', name: 'Forcing a Rim Lockset with an S&D Tool, a Rex Tool, or a K Tool', standard: '6.3.4'},
            {code: 'jb-15-13', label: '15-13', name: 'Using a Shove Knife on a Bored or Mortise Lockset in an Outward-Swinging Door', standard: '6.3.4'},
            {code: 'jb-15-14', label: '15-14', name: 'Using a J Tool on Panic Hardware on an Outward-Swinging Door', standard: '6.3.4'},
            {code: 'jb-15-15', label: '15-15', name: 'Cutting a Latch with a Rotary Saw', standard: '6.3.4'},
            {code: 'jb-15-16', label: '15-16', name: 'Cutting a Padlock with a Rotary Saw', standard: '6.3.4'},
            {code: 'jb-15-17', label: '15-17', name: 'Cutting a Hockey Puck Lock with a Rotary Saw', standard: '6.3.4'},
            {code: 'jb-15-18', label: '15-18', name: 'Making a Triangle Cut in an Overhead Door with a Rotary Saw', standard: '6.3.4'},
            {code: 'jb-15-19', label: '15-19', name: 'Making an Overhead Door Rectangle Box Cut with a Rotary Saw', standard: '6.3.4'},
            {code: 'jb-15-20', label: '15-20', name: 'Making a Rectangle Hinged Cut in an Overhead Door with a Rotary Saw', standard: '6.3.4'},
            {code: 'jb-15-21', label: '15-21', name: 'Cutting a Rolling Steel Door with a Rotary Saw Using the Slice and Pull Method', standard: '6.3.4'},
            {code: 'jb-15-22', label: '15-22', name: 'Forcing Wood Frame Single- and Double-Hung Windows', standard: '6.3.4'},
            {code: 'jb-15-23', label: '15-23', name: 'Forcing Vinyl-Frame Single- and Double-Hung Windows', standard: '6.3.4'},
            {code: 'jb-15-24', label: '15-24', name: 'Breaching a Stud Wall', standard: '6.3.4'}
        ]
    },
    {
        chapter: 16,
        label: 'Ventilation',
        group: 'ff1',
        skillKey: 'ch16Ventilation',
        module: 5,
        exam: 5,
        drills: [
            {code: 'jb-16-1', label: '16-1', name: 'Breaking Glass with a Hand Tool', standard: '6.3.11'},
            {code: 'jb-16-2', label: '16-2', name: 'Breaking Windows with a Ladder', standard: '6.3.11'},
            {code: 'jb-16-3', label: '16-3', name: 'Delivering Negative-Pressure Ventilation', standard: '6.3.11'},
            {code: 'jb-16-4', label: '16-4', name: 'Delivering Positive-Pressure Ventilation', standard: '6.3.12'},
            {code: 'jb-16-5', label: '16-5', name: 'Delivering Hydraulic Ventilation', standard: '6.3.12'},
            {code: 'jb-16-6', label: '16-6', name: 'Operating a Power Saw', standard: '6.3.12'},
            {code: 'jb-16-7', label: '16-7', name: 'Making a Rectangular or Square Cut', standard: '6.3.12'},
            {code: 'jb-16-8', label: '16-8', name: 'Making a Seven, Nine, Eight (7, 9, 8) Rectangular Cut', standard: '6.3.12'},
            {code: 'jb-16-9', label: '16-9', name: 'Making a Louver Cut', standard: '6.3.12'},
            {code: 'jb-16-10', label: '16-10', name: 'Making a Triangular Cut', standard: '6.3.12'},
            {code: 'jb-16-11', label: '16-11', name: 'Making a Peak Cut', standard: '6.3.12'},
            {code: 'jb-16-12', label: '16-12', name: 'Making a Trench Cut', standard: '6.3.12'},
            {code: 'jb-16-13', label: '16-13', name: 'Performing a Readiness Check on a Power Saw', standard: '6.5.1'},
            {code: 'jb-16-14', label: '16-14', name: 'Maintaining a Power Saw', standard: '6.5.1'}
        ]
    },
    {
        chapter: 17,
        label: 'Search and Rescue',
        matrixLabel: 'Search & Rescue',
        group: 'ff1',
        skillKey: 'ch17SearchRescue',
        module: 5,
        exam: 5,
        drills: [
            {code: 'jb-17-1', label: '17-1', name: 'Conducting a Primary Search Using the Standard Search Method', standard: '6.3.9'},
            {code: 'jb-17-2', label: '17-2', name: 'Conducting a Primary Search Using the Oriented Search Method', standard: '6.3.9'},
            {code: 'jb-17-3', label: '17-3', name: 'Performing a Secondary Search', standard: '6.3.9'},
            {code: 'jb-17-4', label: '17-4', name: 'Performing a One-Person Walking Assist', standard: '6.3.9'},
            {code: 'jb-17-5', label: '17-5', name: 'Performing a Two-Person Walking Assist', standard: '6.3.9'},
            {code: 'jb-17-6', label: '17-6', name: 'Performing a Two-Person Extremity Carry', standard: '6.3.9'},
            {code: 'jb-17-7', label: '17-7', name: 'Performing a Two-Person Seat Carry', standard: '6.3.9'},
            {code: 'jb-17-8', label: '17-8', name: 'Performing a Two-Person Chair Carry', standard: '6.3.9'},
            {code: 'jb-17-9', label: '17-9', name: 'Performing a Cradle-in-Arms Carry', standard: '6.3.9'},
            {code: 'jb-17-10', label: '17-10', name: 'Performing a Clothes Drag', standard: '6.3.9'},
            {code: 'jb-17-11', label: '17-11', name: 'Performing a Blanket Drag', standard: '6.3.9'},
            {code: 'jb-17-12', label: '17-12', name: 'Performing a Standing Drag', standard: '6.3.9'},
            {code: 'jb-17-13', label: '17-13', name: 'Performing a Webbing Sling Drag', standard: '6.3.9'},
            {code: 'jb-17-14', label: '17-14', name: 'Performing a Firefighter Drag', standard: '6.3.9'},
            {code: 'jb-17-15', label: '17-15', name: 'Performing an Emergency Drag from a Vehicle', standard: '6.3.9'},
            {code: 'jb-17-16', label: '17-16', name: 'Rescuing a Conscious Victim from a Window', standard: '6.3.9'},
            {code: 'jb-17-17', label: '17-17', name: 'Rescuing an Unconscious Victim from a Window', standard: '6.3.9'},
            {code: 'jb-17-18', label: '17-18', name: 'Rescuing an Unconscious Child or a Small Adult from a Window', standard: '6.3.9'},
            {code: 'jb-17-19', label: '17-19', name: 'Rescuing a Large Adult from a Window', standard: '6.3.9'}
        ]
    },
    {
        chapter: 18,
        label: 'Salvage and Overhaul',
        matrixLabel: 'Salvage & Overhaul',
        width: 110,
        group: 'ff1',
        skillKey: 'ch18Salvage',
        module: 6,
        exam: 6,
        drills: [
            {code: 'jb-18-1', label: '18-1', name: 'Illuminating an Emergency Scene', standard: '6.3.17'},
            {code: 'jb-18-2', label: '18-2', name: 'Using Sprinkler Wedges', standard: '6.3.14'},
            {code: 'jb-18-3', label: '18-3', name: 'Using a Sprinkler Stop', standard: '6.3.14'},
            {code: 'jb-18-4', label: '18-4', name: 'Closing and Reopening a Main OS&Y Valve', standard: '6.3.14'},
            {code: 'jb-18-5', label: '18-5', name: 'Closing and Reopening a Main Butterfly Valve', standard: '6.3.14'},
            {code: 'jb-18-6', label: '18-6', name: 'Closing and Reopening a Main Post Indicator Valve', standard: '6.3.14'},
            {code: 'jb-18-7', label: '18-7', name: 'Constructing a Water Chute', standard: '6.3.14'},
            {code: 'jb-18-8', label: '18-8', name: 'Constructing a Water Catch-All', standard: '6.3.14'},
            {code: 'jb-18-9', label: '18-9', name: 'Performing a Salvage Cover Fold for One-Firefighter Deployment', standard: '6.3.14'},
            {code: 'jb-18-10', label: '18-10', name: 'Performing a Salvage Cover Fold for Two-Firefighter Deployment', standard: '6.3.14'},
            {code: 'jb-18-11', label: '18-11', name: 'Folding and Rolling a Salvage Cover', standard: '6.3.14'},
            {code: 'jb-18-12', label: '18-12', name: 'Deploying a Salvage Cover with the One-Person Salvage Cover Roll', standard: '6.3.14'},
            {code: 'jb-18-13', label: '18-13', name: 'Performing a One-Firefighter Salvage Cover Shoulder Toss', standard: '6.3.14'},
            {code: 'jb-18-14', label: '18-14', name: 'Performing a Two-Firefighter Salvage Cover Balloon Toss', standard: '6.3.14'},
            {code: 'jb-18-15', label: '18-15', name: 'Using a Multi-Gas Meter', standard: '6.3.21'},
            {code: 'jb-18-16', label: '18-16', name: 'Pulling a Ceiling Using a Pike Pole', standard: '6.3.13'},
            {code: 'jb-18-17', label: '18-17', name: 'Opening an Interior Wall', standard: '6.3.13'}
        ]
    },
    {
        chapter: 19,
        label: 'Firefighter Survival',
        group: 'ff1',
        skillKey: 'ch19Survival',
        module: 6,
        exam: 6,
        drills: [
            {code: 'jb-19-1', label: '19-1', name: 'Initiating a Mayday Call for Emergency Assistance', standard: '6.2.3'},
            {code: 'jb-19-2', label: '19-2', name: 'Performing Self-Rescue Using a Hose Line', standard: '6.3.5'},
            {code: 'jb-19-3', label: '19-3', name: 'Locating a Door or Window for Emergency Exit', standard: '6.3.5'},
            {code: 'jb-19-4', label: '19-4', name: 'Using the Backwards Swim Technique to Escape Through a Wall', standard: '6.3.1, 6.3.5, 6.3.9'},
            {code: 'jb-19-5', label: '19-5', name: 'Using the Forward Swim Technique to Escape through a Wall', standard: '6.3.1, 6.3.5, 6.3.9'},
            {code: 'jb-19-6', label: '19-6', name: 'Escaping from an Entanglement', standard: '6.3.1, 6.3.5, 6.3.9'},
            {code: 'jb-19-7', label: '19-7', name: 'Rescuing a Downed Firefighter Using the Waist Straps of the SCBA Harness as a Rescue Harness', standard: '6.3.9'},
            {code: 'jb-19-8', label: '19-8', name: 'Rescuing a Downed Firefighter Using a Drag Rescue Device', standard: '6.3.9'},
            {code: 'jb-19-9', label: '19-9', name: 'Rescuing a Downed Firefighter as a Two-Person Team', standard: '6.3.9'},
            {code: 'jb-19-10', label: '19-10', name: 'Supplying Air to a Downed Firefighter Using the Low-Pressure Hose from a Rapid Intervention Pack', standard: '6.3.9'},
            {code: 'jb-19-11', label: '19-11', name: 'Supplying Air to a Downed Firefighter Using the Emergency Breathing Safety System', standard: '6.3.9'}
        ]
    },
    {
        chapter: 20,
        label: 'Firefighter Rehabilitation',
        matrixLabel: 'FF Rehabilitation',
        width: 130,
        group: 'ff1',
        skillKey: 'ch20Rehab',
        module: 6,
        exam: 6,
        theoryOnly: true,
        drills: []
    },
    {
        chapter: 21,
        label: 'Wildland and Ground Cover Fires',
        matrixLabel: 'Wildland & Ground Fires',
        width: 140,
        group: 'ff1',
        skillKey: 'ch21Wildland',
        module: 6,
        exam: 6,
        drills: [
            {code: 'jb-21-1', label: '21-1', name: 'Suppressing a Wildland Fire', standard: '6.3.19'}
        ]
    }
];

const FF2_CHAPTERS = [
    {
        chapter: 22,
        label: 'Establishing and Transferring Command',
        matrixLabel: 'Establish & Transfer Command',
        width: 160,
        group: 'ff2',
        skillKey: 'ch22Command',
        module: 7,
        exam: 7,
        drills: [
            {code: 'jb-22-1', label: '22-1', name: 'Operating Within the ICS', standard: '7.1.1 and 7.1.2'},
            {code: 'jb-22-2', label: '22-2', name: 'Establishing Command', standard: '7.1.1 and 7.1.2'},
            {code: 'jb-22-3', label: '22-3', name: 'Transferring Command', standard: '7.1.2'}
        ]
    },
    {
        chapter: 23,
        label: 'Advanced Fire Suppression',
        width: 140,
        group: 'ff2',
        skillKey: 'ch23AdvSuppression',
        module: 7,
        exam: 7,
        drills: [
            {code: 'jb-23-1', label: '23-1', name: 'Using a TIC at a Structure Fire', standard: '7.3.3'},
            {code: 'jb-23-2', label: '23-2', name: 'Coordinating an Interior Attack', standard: '7.3.2'},
            {code: 'jb-23-3', label: '23-3', name: 'Suppressing a Flammable Gas Cylinder Fire', standard: '7.3.4'},
            {code: 'jb-23-4', label: '23-4', name: 'Operating an In-Line Foam Eductor', standard: '7.3.1'},
            {code: 'jb-23-5', label: '23-5', name: 'Performing the Roll-In Method of Applying Foam', standard: '7.3.1'},
            {code: 'jb-23-6', label: '23-6', name: 'Performing the Bounce-Off Method of Applying Foam', standard: '5.3.1'},
            {code: 'jb-23-7', label: '23-7', name: 'Performing the Rain-Down Method of Applying Foam', standard: '7.3.1'},
            {code: 'jb-23-8', label: '23-8', name: 'Performing an Annual Service Test on a Fire Hose', standard: '7.5.5'}
        ]
    },
    {
        chapter: 24,
        label: 'Systems of Fire Detection, Suppression, and Smoke Control',
        matrixLabel: 'Detection/Suppression Systems',
        width: 180,
        group: 'ff2',
        skillKey: 'ch24Detection',
        module: 7,
        exam: 7,
        theoryOnly: true,
        drills: []
    },
    {
        chapter: 25,
        label: 'Fire and Life Safety Initiatives',
        matrixLabel: 'Fire & Life Safety',
        width: 110,
        group: 'ff2',
        skillKey: 'ch25LifeSafety',
        module: 8,
        exam: 8,
        drills: [
            {code: 'jb-25-1', label: '25-1', name: 'Teaching E.D.I.T.H.', standard: '7.5.2'},
            {code: 'jb-25-2', label: '25-2', name: 'Teaching Fire Safety to the Public', standard: '7.5.2'},
            {code: 'jb-25-3', label: '25-3', name: 'Conducting a Fire Station Tour', standard: '7.5.2'},
            {code: 'jb-25-4', label: '25-4', name: 'Installing and Maintaining Smoke Alarms', standard: '7.5.1, 7.5.2'},
            {code: 'jb-25-5', label: '25-5', name: 'Conducting a Fire Safety Survey', standard: '7.5.1'},
            {code: 'jb-25-6', label: '25-6', name: 'Conducting a Pre-Incident Survey', standard: '7.5.3'}
        ]
    },
    {
        chapter: 26,
        label: 'Vehicle Rescue and Extrication',
        matrixLabel: 'Vehicle Rescue & Extrication',
        width: 140,
        group: 'ff2',
        skillKey: 'ch26Extrication',
        module: 8,
        exam: 8,
        drills: [
            {code: 'jb-26-1', label: '26-1', name: 'Performing a Scene Size-Up at a Motor Vehicle Accident', standard: '7.4.1'},
            {code: 'jb-26-2', label: '26-2', name: 'Immobilize and Disable the Electrical System of a Vehicle Following a Motor Vehicle Accident', standard: '7.4.1'},
            {code: 'jb-26-3', label: '26-3', name: 'Stabilizing a Vehicle Resting on All Four Wheels Following a Motor Vehicle Accident', standard: '7.4.1'},
            {code: 'jb-26-4', label: '26-4', name: 'Breaking Tempered Glass', standard: '7.4.1'},
            {code: 'jb-26-5', label: '26-5', name: 'Forcing and Removing a Vehicle Door', standard: '7.4.1'},
            {code: 'jb-26-6', label: '26-6', name: 'Providing Medical Care to a Victim in a Vehicle', standard: '7.4.1'},
            {code: 'jb-26-7', label: '26-7', name: 'Removing the Windshield from a Vehicle', standard: '7.4.1'},
            {code: 'jb-26-8', label: '26-8', name: 'Performing a Dash Roll', standard: '7.4.1'},
            {code: 'jb-26-9', label: '26-9', name: 'Performing a Dash Lift', standard: '7.4.1'},
            {code: 'jb-26-10', label: '26-10', name: 'Removing the Roof of a Vehicle', standard: '7.4.1'}
        ]
    },
    {
        chapter: 27,
        label: 'Assisting Special Rescue Teams',
        matrixLabel: 'Assisting Special Rescue',
        width: 140,
        group: 'ff2',
        skillKey: 'ch27SpecialRescue',
        module: 8,
        exam: 8,
        drills: [
            {code: 'jb-27-1', label: '27-1', name: 'Establishing a Barrier', standard: '7.4.2'},
            {code: 'jb-27-2', label: '27-2', name: 'Identifying and Retrieving Rescue Tools', standard: '7.4.2'},
            {code: 'jb-27-3', label: '27-3', name: 'Conducting a Weekly/Monthly Generator Test', standard: '7.5.4'}
        ]
    },
    {
        chapter: 28,
        label: 'Understanding Fire Investigation',
        matrixLabel: 'Fire Investigation',
        width: 130,
        group: 'ff2',
        skillKey: 'ch28Investigation',
        module: 8,
        exam: 8,
        theoryOnly: true,
        drills: []
    }
];
// One written exam per module, covering every chapter in it.
const examsFor = chapters => [...new Set(chapters.map(ch => ch.module))].map(module => ({
    exam: module,
    label: `Module ${module} Written Exam`,
    chapters: chapters.filter(ch => ch.module === module).map(ch => ch.chapter)
}));

const sectionBase = {
    ladder: LADDER,
    documentSet: 'jb-1010',
    sheetEdition: 'jb-ca5'
};

const Nfpa1010SkillDrills = {
    ff1: {
        ...sectionBase,
        key: 'ff1',
        label: 'Firefighter I',
        order: 1,
        chapters: FF1_CHAPTERS,
        exams: examsFor(FF1_CHAPTERS)
    },
    ff2: {
        ...sectionBase,
        key: 'ff2',
        label: 'Firefighter II',
        order: 2,
        chapters: FF2_CHAPTERS,
        exams: examsFor(FF2_CHAPTERS)
    }
};

module.exports = Nfpa1010SkillDrills;
